/**
 * GroundingSection - Combined component that renders all grounding UI
 * 
 * This is the main entry point for displaying Phase 0 grounding features
 * in a message. It intelligently combines and layouts:
 * - Confidence indicator
 * - Grounded claims
 * - Knowledge gaps
 * - Clarifying questions
 */

import React from 'react';
import type { 
  GroundedClaim, 
  KnowledgeGap, 
  ClarifyingQuestion 
} from '../../../api/services/agent';
import { ConfidenceIndicator, ConfidenceBadge } from './ConfidenceIndicator';
import { GroundedClaimsList } from './GroundedClaimCard';
import { KnowledgeGapIndicator } from './KnowledgeGapIndicator';
import { ClarifyingQuestionsList } from './ClarifyingQuestionCard';

interface GroundingSectionProps {
  groundedClaims?: GroundedClaim[];
  knowledgeGaps?: KnowledgeGap[];
  needsClarification?: boolean;
  clarifyingQuestions?: ClarifyingQuestion[];
  overallConfidence?: number;
  onSourceClick?: (artifactId: string) => void;
  onAnswerClarification?: (question: ClarifyingQuestion, answer: string) => void;
  onUploadDocument?: () => void;
  onFindExpert?: () => void;
  isLoading?: boolean;
  compact?: boolean;
  className?: string;
}

export const GroundingSection: React.FC<GroundingSectionProps> = ({
  groundedClaims = [],
  knowledgeGaps = [],
  needsClarification = false,
  clarifyingQuestions = [],
  overallConfidence,
  onSourceClick,
  onAnswerClarification,
  onUploadDocument,
  onFindExpert,
  isLoading = false,
  compact = false,
  className = '',
}) => {
  const hasClaims = groundedClaims.length > 0;
  const hasGaps = knowledgeGaps.length > 0;
  const hasClarifications = needsClarification && clarifyingQuestions.length > 0;
  const hasContent = hasClaims || hasGaps || hasClarifications || overallConfidence !== undefined;

  if (!hasContent) {
    return null;
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Confidence and summary header */}
      {overallConfidence !== undefined && (
        <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-[color:var(--color-neutral-50)] border border-[color:var(--color-neutral-100)]">
          <div className="flex items-center gap-3">
            <span className="text-[12px] font-medium text-[color:var(--color-neutral-600)]">
              Response confidence
            </span>
            <ConfidenceIndicator 
              confidence={overallConfidence} 
              showLabel 
              size="md"
            />
          </div>
          
          {/* Quick stats */}
          <div className="flex items-center gap-3 text-[11px] text-[color:var(--color-neutral-500)]">
            {hasClaims && (
              <span className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5 text-[color:var(--color-success-500)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {groundedClaims.length} verified
              </span>
            )}
            {hasGaps && (
              <span className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5 text-[color:var(--color-warning-500)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                {knowledgeGaps.length} gaps
              </span>
            )}
          </div>
        </div>
      )}

      {/* Clarifying questions - show first as they need attention */}
      {hasClarifications && onAnswerClarification && (
        <ClarifyingQuestionsList
          questions={clarifyingQuestions}
          onAnswer={onAnswerClarification}
          isLoading={isLoading}
        />
      )}

      {/* Knowledge gaps */}
      {hasGaps && (
        <KnowledgeGapIndicator
          gaps={knowledgeGaps}
          onUploadDocument={onUploadDocument}
          onSuggestExpert={onFindExpert}
        />
      )}

      {/* Grounded claims - collapsible section */}
      {hasClaims && (
        <GroundedClaimsList
          claims={groundedClaims}
          onSourceClick={onSourceClick}
          compact={compact}
          maxVisible={compact ? 2 : 3}
        />
      )}
    </div>
  );
};

/**
 * CompactGroundingBadge - Minimal indicator for message headers
 * Shows at-a-glance grounding status
 */
interface CompactGroundingBadgeProps {
  groundedClaims?: GroundedClaim[];
  knowledgeGaps?: KnowledgeGap[];
  overallConfidence?: number;
  className?: string;
}

export const CompactGroundingBadge: React.FC<CompactGroundingBadgeProps> = ({
  groundedClaims = [],
  knowledgeGaps = [],
  overallConfidence,
  className = '',
}) => {
  const hasClaims = groundedClaims.length > 0;
  const hasGaps = knowledgeGaps.length > 0;

  if (!hasClaims && !hasGaps && overallConfidence === undefined) {
    return null;
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {overallConfidence !== undefined && (
        <ConfidenceBadge confidence={overallConfidence} />
      )}
      
      {hasClaims && (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium"
          style={{
            backgroundColor: 'var(--color-success-50)',
            color: 'var(--color-success-700)',
          }}
        >
          <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {groundedClaims.length}
        </span>
      )}
      
      {hasGaps && (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium"
          style={{
            backgroundColor: 'var(--color-warning-50)',
            color: 'var(--color-warning-700)',
          }}
        >
          <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01" />
          </svg>
          {knowledgeGaps.length}
        </span>
      )}
    </div>
  );
};

export default GroundingSection;

